import * as yup from "yup";
import Link from "next/link";
import Image from "next/image";
import Toast from "@components/toast";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import { useForm } from "react-hook-form";
import { useSession } from "next-auth/react";
import { useSettings } from "@hooks/settings";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

const schema = yup.object().shape({
  email: yup.string().email().required(),
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  password: yup.string().min(8).required(),
  otp: yup.string().required("OTP is required"),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required(),
});

const emailSchema = yup.object().shape({
  email: yup.string().email().required("Email is required"),
  otp: yup.string().required("OTP is required"),
});

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["auth"])),
    },
  };
}

export default function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [conditions, setConditions] = useState({});
  const [referral, setReferral] = useState(null);
  const [otpEmail, setOtpEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [next, setNext] = useState(false);
  const { data: session, status } = useSession();
  const { t } = useTranslation("auth");
  const router = useRouter();
  const notify = React.useCallback((type, message) => {
    Toast({ type, message });
  }, []);
  const settings = useSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: errorsEmail },
    trigger,
    reset: resetEmail,
    getValues: getValuesEmail,
  } = useForm({
    resolver: yupResolver(emailSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    fetch("/api/auth/signup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...data,
        referral,
      }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.json();
        throw new Error(text.error);
      })
      .then(async (response) => {
        notify("success", response.message);

        await signIn("credentials", {
          redirect: false,
          email: data.email,
          password: data.password,
          callbackUrl: router.query.callbackUrl
            ? router.query.callbackUrl
            : window.location.origin,
        });
      })
      .catch((error) => {
        notify("error", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const sendOTP = async (email) => {
    if (!email) return;
    setLoading(true);
    fetch("/api/auth/signup/send-otp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.json();
        throw new Error(text.error);
      })
      .then((data) => {
        setOtpEmail(email);
        notify("success", data.message);
      })
      .catch((err) => {
        notify("error", err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const verifyOTP = async (data) => {
    setLoading(true);
    fetch("/api/auth/signup/verify-otp", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: data.email, otp: data.otp }),
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.json();
        throw new Error(text.error);
      })
      .then((res) => {
        reset({
          email: data.email,
          otp: data.otp,
        });
        setNext(true);
        notify("success", res.message);
      })
      .catch((err) => {
        notify("error", err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    rewardful("ready", function () {
      setReferral(Rewardful.referral);
    });
  }, []);

  if (status != "loading" && session) {
    notify("info", t("auth:alreadySignedIn"));

    router.query.callbackUrl
      ? router.push(router.query.callbackUrl)
      : router.push(
          `${
            router.locale === router.defaultLocale ? "" : `/${router.locale}`
          }/${session?.user?.language}/dashboard`
        );

    return null;
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const checkPasswordStrength = (value) => {
    const hasMinimumLength = value.length >= 8;
    const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);

    return {
      strength:
        (hasMinimumLength +
          hasSpecialCharacter +
          hasNumber +
          hasUppercase +
          hasLowercase) /
        5,
      conditions: {
        minimumLength: hasMinimumLength,
        specialCharacter: hasSpecialCharacter,
        number: hasNumber,
        uppercase: hasUppercase,
        lowercase: hasLowercase,
      },
    };
  };

  const handlePasswordChange = (event) => {
    const { strength, conditions } = checkPasswordStrength(event.target.value);
    setConditions(conditions);
  };

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="d-none d-md-block col-md-8 col-lg-6 col-xl-3">
          <Image
            src="/images/sign-up-animate.svg"
            alt="Sign Up"
            width={300}
            height={300}
            priority
            className="d-block mx-auto mb-4"
            style={{ width: "auto", height: "auto" }}
          />
        </div>

        <div className="col-12 col-md-8 col-lg-6 col-xl-4">
          <div className="card">
            <div className="card-header">
              <Image
                src={settings.app_logo}
                alt={settings.app_name}
                width={100}
                height={100}
                priority
                className="d-block mx-auto mb-4"
                style={{ width: "auto", height: "auto" }}
              />
              <h1 className="display-6 text-center mb-3">
                {t("auth:signUp.title")}
              </h1>
            </div>

            <div className="card-body">
              {next ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <input hidden {...register("otp")} />
                  <div className="mb-3">
                    <label className="form-label">
                      {t("auth:fields.email.label")}
                    </label>
                    <input
                      className="form-control form-control-lg"
                      type="email"
                      {...register("email")}
                      disabled
                    />
                    {errors.email && (
                      <div className="text-danger">{errors.email.message}</div>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label">
                        {t("auth:fields.first-name")}
                        <i className="fas fa-asterisk text-danger ms-1" />
                      </label>
                      <input
                        className="form-control form-control-lg"
                        type="text"
                        {...register("first_name")}
                      />

                      <div className="text-danger">
                        {errors?.first_name?.message}
                      </div>
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label">
                        {t("auth:fields.last-name")}
                        <i className="fas fa-asterisk text-danger ms-1" />
                      </label>
                      <input
                        className="form-control form-control-lg"
                        type="text"
                        {...register("last_name")}
                      />

                      <div className="text-danger">
                        {errors?.last_name?.message}
                      </div>
                    </div>
                  </div>

                  {/* password */}
                  <div className="mb-3">
                    <label className="form-label">
                      {t("auth:fields.password.label")}
                      <i className="fas fa-asterisk text-danger ms-1" />
                    </label>
                    <div className="input-group flex-nowrap">
                      <input
                        className="form-control"
                        type={showPassword ? "text" : "password"}
                        {...register("password")}
                        onChange={handlePasswordChange}
                      />
                      <span className="input-group-text" id="addon-wrapping">
                        <i
                          className={`far 
                            ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                          onClick={togglePasswordVisibility}
                        />
                      </span>
                    </div>

                    <ul className="list-unstyled row mt-2">
                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.uppercase
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-uppercase")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.lowercase
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-lowercase")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.specialCharacter
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-special-character")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.number
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:contains-number")}
                      </li>

                      <li className="col-6">
                        <i
                          className={`me-1 far 
                          ${
                            conditions?.minimumLength
                              ? "fa-check-circle text-success"
                              : "fa-times-circle text-danger"
                          }
                          `}
                        />
                        {t("auth:8-characters")}
                      </li>
                    </ul>

                    {errors.password && (
                      <div className="text-danger">
                        {errors.password.message}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      {t("auth:fields.passwordConfirm.label")}
                      <i className="fas fa-asterisk text-danger ms-1" />
                    </label>
                    <input
                      className="form-control form-control-lg"
                      type="password"
                      {...register("passwordConfirm")}
                    />
                    {errors.passwordConfirm && (
                      <div className="text-danger">
                        {errors.passwordConfirm.message}
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="submit"
                      className="btn btn-lg btn-bota-light-blue"
                      disabled={loading}
                    >
                      {t("auth:signUp.title")}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmitEmail(verifyOTP)}>
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-6 mb-3">
                      <label className="form-label">
                        {t("auth:fields.email.label")}
                        <i className="fas fa-asterisk text-danger ms-1" />
                      </label>
                      <input
                        className="form-control form-control-lg"
                        type="email"
                        {...registerEmail("email", {
                          onChange: () => {
                            setOtpEmail(null);
                          },
                        })}
                      />
                      {errorsEmail.email && (
                        <div className="text-danger">
                          {errorsEmail.email.message}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-md-12 mb-3">
                      <div className="text-center mt-4">
                        <button
                          type="button"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                          onClick={async () => {
                            const result = await trigger("email");
                            result && sendOTP(getValuesEmail("email"));
                          }}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-grow spinner-grow-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Loading...
                            </>
                          ) : otpEmail ? (
                            t("auth:resendOTP")
                          ) : (
                            t("auth:verifyEmail")
                          )}
                        </button>
                      </div>
                    </div>
                    {otpEmail && (
                      <>
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">
                            {t("auth:fields.otp.label")}
                            <span className="text-danger ms-1">
                              <i className="fas fa-asterisk fa-xs" />
                            </span>
                          </label>
                          <input
                            className="form-control form-control-lg"
                            type="text"
                            {...registerEmail("otp")}
                          />
                          {errorsEmail.otp && (
                            <div className="text-danger">
                              {errorsEmail.otp.message}
                            </div>
                          )}
                        </div>
                        <div className="col-12 col-md-12 mb-3">
                          <div className="text-end mt-4">
                            <button
                              type="submit"
                              className="btn btn-lg btn-primary"
                              disabled={loading}
                            >
                              {t("auth:next")}
                              <i className="fas fa-arrow-right ms-2" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </form>
              )}

              <div className="row jsutify-content-end">
                <div className="col-12 mb-3">
                  <p className="text-end">
                    <small className="text-muted text-center">
                      {t("auth:alreadyHaveAccount")}{" "}
                      <Link
                        href={
                          router.query.callbackUrl
                            ? `/auth/signin?callbackUrl=${encodeURIComponent(
                                router.query.callbackUrl
                              )}`
                            : "/auth/signin"
                        }
                        style={{
                          color: "var(--bs-bota-light-blue)",
                        }}
                      >
                        {t("auth:signIn.title")}
                      </Link>
                    </small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
